import React, { useEffect } from 'react';
import { toast } from 'react-toastify';

import NewVersionToast from 'components/New-Version-Toast';

const CHECK_INTERVAL = 5 * 60 * 1000;
const MIN_CHECK_INTERVAL = 60 * 1000;
const TOAST_ID = 'new-version';

const getScriptSources = (doc) =>
  Array.from(doc.querySelectorAll('script[src]'))
    .map((script) => new URL(script.getAttribute('src'), window.location.origin).pathname)
    .filter((src) => src.startsWith('/static/js/'));

// A deploy changes the hashed bundle names referenced by index.html (served with no-cache), so a
// script listed there that this page never loaded means a new version is available.
const hasNewVersion = async () => {
  const response = await fetch('/', { cache: 'no-cache' });
  if (!response.ok) return false;

  const html = await response.text();
  const latestScripts = getScriptSources(new DOMParser().parseFromString(html, 'text/html'));
  const loadedScripts = getScriptSources(document);

  return latestScripts.some((src) => !loadedScripts.includes(src));
};

export const useNewVersionCheck = () => {
  useEffect(() => {
    let lastCheck = 0;

    const check = async () => {
      if (document.visibilityState !== 'visible' || toast.isActive(TOAST_ID)) return;
      // Switching back to the tab often would otherwise fetch index.html on every return
      if (Date.now() - lastCheck < MIN_CHECK_INTERVAL) return;
      lastCheck = Date.now();

      try {
        if (!(await hasNewVersion())) return;
      } catch (error) {
        return;
      }

      toast.info(<NewVersionToast />, {
        toastId: TOAST_ID,
        autoClose: false,
        closeOnClick: false,
        className: 'new-version-toast',
      });
    };

    const interval = setInterval(check, CHECK_INTERVAL);
    document.addEventListener('visibilitychange', check);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);
};
